import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge, Button, StatusDot } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import {
  useActivateTemplateVersion,
  useCreateTemplateVersion,
  useTemplate,
  useTestTemplate,
} from '@/hooks/useTemplates';
import { ApiError } from '@/services/api';
import { detalheLegivel } from '@/services/erros';
import type { ConfigurableField, TemplateDetail, TestResult } from '@/types/template';

import styles from './TemplateEditor.module.css';

/**
 * O 422 de POST /templates/{id}/test NAO e falha do sistema: e o backend
 * dizendo QUAIS variaveis do prompt ficaram sem amostra. A tela mostrava so
 * "ApiError: 422" e o operador nao tinha onde preencher (F-492).
 */
interface PedidoDeAmostra {
  titulo: string | null;
  comoResolver: string | null;
  variaveis: string[];
}

function pedidoDeAmostra(err: unknown): PedidoDeAmostra | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const d = detail as { erro?: unknown; variaveis?: unknown; como_resolver?: unknown };
  const variaveis = Array.isArray(d.variaveis)
    ? d.variaveis.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    : [];
  if (variaveis.length === 0) return null;
  return {
    titulo: typeof d.erro === 'string' ? d.erro : null,
    comoResolver: typeof d.como_resolver === 'string' ? d.como_resolver : null,
    variaveis,
  };
}

export function TemplateEditor() {
  const { t } = useTranslation(['config', 'common']);
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const { data: template, isLoading } = useTemplate(id);

  const [configurable, setConfigurable] = useState<Record<string, unknown>>({});
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [amostraPedida, setAmostraPedida] = useState<PedidoDeAmostra | null>(null);
  const [amostra, setAmostra] = useState<Record<string, string>>({});

  const createVersionMutation = useCreateTemplateVersion();
  const activateVersionMutation = useActivateTemplateVersion();
  const testMutation = useTestTemplate();

  // Initialise configurable from active version
  useEffect(() => {
    if (!template) return;
    const activeVersion = template.versions.find(
      (v) => v.version_number === template.current_version_number,
    );
    setConfigurable(activeVersion?.configurable ?? {});
  }, [template]);

  // Trocar de template pela rota :id NAO remonta este componente — sem limpar,
  // a amostra digitada para um prompt seguiria para o prompt seguinte.
  useEffect(() => {
    setAmostraPedida(null);
    setAmostra({});
  }, [id]);

  if (isLoading) return <p>{t('common:status.loading')}</p>;
  if (!template) return <p>{t('common:status.notFound')}</p>;

  const setField = (key: string, value: unknown) => {
    setConfigurable((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveVersion = async () => {
    setStatusMsg(null);
    try {
      const v = await createVersionMutation.mutateAsync({
        id,
        payload: { configurable },
      });
      setStatusMsg(
        t('config:template_editor.msg_version_created', {
          version: v.version_number,
        }),
      );
    } catch (e) {
      setStatusMsg(t('config:template_editor.msg_error', { error: detalheLegivel(e) }));
    }
  };

  const handleActivate = async (versionId: string, versionNumber: number) => {
    setStatusMsg(null);
    try {
      await activateVersionMutation.mutateAsync({ id, versionId });
      setStatusMsg(
        t('config:template_editor.msg_version_activated', {
          version: versionNumber,
        }),
      );
    } catch (e) {
      setStatusMsg(t('config:template_editor.msg_error', { error: detalheLegivel(e) }));
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    setStatusMsg(null);
    try {
      const result = await testMutation.mutateAsync({
        id,
        // So vai o que o operador escreveu: campo vazio substituiria o
        // {{placeholder}} por nada e gastaria uma chamada de IA com o prompt
        // mutilado — exatamente o que o 422 existe para evitar.
        payload: {
          configurable,
          sample_variables: Object.fromEntries(
            Object.entries(amostra).filter(([, v]) => v.trim() !== ''),
          ),
        },
      });
      setTestResult(result);
    } catch (e) {
      const pedido = pedidoDeAmostra(e);
      if (pedido) {
        setAmostraPedida((prev) => ({
          titulo: pedido.titulo,
          comoResolver: pedido.comoResolver,
          // Uniao com o pedido anterior: uma variavel ja preenchida some da
          // lista do backend (foi substituida) e o campo dela desapareceria da
          // tela, so para o valor voltar a faltar no clique seguinte.
          variaveis: [...new Set([...(prev?.variaveis ?? []), ...pedido.variaveis])],
        }));
        setAmostra((prev) => {
          const next = { ...prev };
          for (const nome of pedido.variaveis) next[nome] ??= '';
          return next;
        });
      }
      setStatusMsg(t('config:template_editor.msg_test_error', { error: detalheLegivel(e) }));
    }
  };

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/config/prompts" className={styles.back}>
          {t('config:template_editor.back_link')}
        </Link>
        <h1 className={styles.title}>{template.name}</h1>
        {template.current_version_number !== null && (
          <Badge tone="info" size="sm">
            {t('config:template_editor.active_badge', {
              version: template.current_version_number,
            })}
          </Badge>
        )}
      </header>

      <p className={styles.warn}>{t('config:template_editor.warn_override')}</p>

      <section className={styles.locked}>
        <h2 className={styles.sectionTitle}>{t('config:template_editor.section_system')}</h2>
        <pre className={styles.code}>{template.system_message}</pre>
      </section>

      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>{t('config:template_editor.section_configurable')}</h2>
        {Object.entries(template.configurable_schema).map(([key, spec]) => (
          <ConfigurableInput
            key={key}
            name={key}
            spec={spec}
            value={configurable[key]}
            onChange={(v) => setField(key, v)}
          />
        ))}
        {Object.keys(template.configurable_schema).length === 0 && (
          <p className={styles.muted}>{t('config:template_editor.empty_configurable')}</p>
        )}
      </section>

      <section className={styles.locked}>
        <h2 className={styles.sectionTitle}>{t('config:template_editor.section_output_schema')}</h2>
        <p className={styles.muted}>
          {t('config:template_editor.format_label')} <code>{template.output_format}</code>
        </p>
        <ul className={styles.validationList}>
          {template.output_validation.map((v, i) => (
            <li key={i}>
              <code>{JSON.stringify(v)}</code>
            </li>
          ))}
        </ul>
      </section>

      {amostraPedida && (
        <section className={styles.card}>
          {amostraPedida.titulo && <h2 className={styles.sectionTitle}>{amostraPedida.titulo}</h2>}
          {amostraPedida.comoResolver && (
            <p className={styles.muted}>{amostraPedida.comoResolver}</p>
          )}
          {amostraPedida.variaveis.map((nome) => (
            <div key={nome} className={styles.field}>
              <label className={styles.label} htmlFor={`amostra-${nome}`}>
                {nome}
              </label>
              <input
                id={`amostra-${nome}`}
                type="text"
                className={styles.input}
                value={amostra[nome] ?? ''}
                onChange={(e) => setAmostra((prev) => ({ ...prev, [nome]: e.target.value }))}
              />
            </div>
          ))}
        </section>
      )}

      <div className={styles.actions}>
        {statusMsg && <span className={styles.statusMsg}>{statusMsg}</span>}
        <Button variant="secondary" onClick={handleTest} loading={testMutation.isPending}>
          {t('config:template_editor.btn_test')}
        </Button>
        <Button onClick={handleSaveVersion} loading={createVersionMutation.isPending}>
          {t('config:template_editor.btn_save_version')}
        </Button>
      </div>

      {testResult && (
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>{t('config:template_editor.section_test_result')}</h2>
          <div className={styles.meta}>
            <span>
              {t('config:template_editor.meta_model', {
                model: testResult.model,
              })}
            </span>
            <span>
              {t('config:template_editor.meta_tokens', {
                input: testResult.input_tokens,
                output: testResult.output_tokens,
              })}
            </span>
            <span>
              {t('config:template_editor.meta_cost', {
                cost: testResult.cost_usd.toFixed(4),
              })}
            </span>
          </div>
          <h3 className={styles.subTitle}>{t('config:template_editor.sub_validation')}</h3>
          <ul className={styles.validationList}>
            {testResult.validations.map((v, i) => (
              <li key={i} className={styles.valItem}>
                <StatusDot state={v.passed ? 'ok' : 'crit'} />
                <code>{v.check}</code>
                {v.detail && <span className={styles.muted}>— {v.detail}</span>}
              </li>
            ))}
          </ul>
          <h3 className={styles.subTitle}>{t('config:template_editor.sub_output')}</h3>
          <pre className={styles.code}>{testResult.output}</pre>
        </section>
      )}

      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>
          {t('config:template_editor.section_versions', {
            count: template.versions.length,
          })}
        </h2>
        <ul className={styles.versionList}>
          {template.versions.map((v) => (
            <li key={v.id} className={styles.versionRow}>
              <Badge
                tone={v.status === 'active' ? 'ok' : v.status === 'draft' ? 'warn' : 'neutral'}
                size="xs"
              >
                v{v.version_number} · {v.status}
              </Badge>
              <span className={styles.muted}>
                {v.model} · temp {v.temperature}
              </span>
              <span className={styles.muted}>{formatarDataHora(v.created_at)}</span>
              {v.status !== 'active' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleActivate(v.id, v.version_number)}
                >
                  {t('common:actions.activate')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

interface ConfigInputProps {
  name: string;
  spec: ConfigurableField;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ConfigurableInput({ name, spec, value, onChange }: ConfigInputProps) {
  const { t } = useTranslation('config');

  if (spec.type === 'select') {
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <select
          value={(value as string) ?? spec.default ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={styles.select}
        >
          {spec.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (spec.type === 'number') {
    return (
      <div className={styles.field}>
        <label className={styles.label}>
          {name} {spec.min !== undefined && `(${spec.min}-${spec.max})`}
        </label>
        <input
          type="number"
          value={(value as number) ?? spec.default ?? 0}
          min={spec.min}
          max={spec.max}
          onChange={(e) => onChange(Number(e.target.value))}
          className={styles.input}
        />
      </div>
    );
  }

  if (spec.type === 'toggle') {
    return (
      <div className={styles.field}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={spec.locked}
            onChange={(e) => onChange(e.target.checked)}
          />
          {name} {spec.locked && <span className={styles.lockedTag}>locked</span>}
        </label>
      </div>
    );
  }

  if (spec.type === 'multi_toggle') {
    const arr = (value as string[]) ?? [];
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <div className={styles.multiToggle}>
          {spec.options.map((o) => (
            <label key={o.value} className={styles.checkbox}>
              <input
                type="checkbox"
                checked={arr.includes(o.value)}
                disabled={o.locked}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...arr, o.value]
                    : arr.filter((v) => v !== o.value);
                  onChange(next);
                }}
              />
              {o.label} {o.locked && <span className={styles.lockedTag}>locked</span>}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (spec.type === 'text_list') {
    const arr = (value as string[]) ?? [];
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <input
          type="text"
          value={arr.join(', ')}
          placeholder={spec.placeholder ?? t('template_editor.placeholder_comma_separated')}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          className={styles.input}
        />
      </div>
    );
  }

  return null;
}

export type { TemplateDetail };
